import { CurrencyPipe } from '@angular/common'
import { ChangeDetectionStrategy, Component, computed, input, signal } from '@angular/core'
import { MatButtonModule } from '@angular/material/button'
import { MatIconModule } from '@angular/material/icon'
import { TranslateModule } from '@ngx-translate/core'

import { MultiVisitReviewCharge } from '../../../services/estimate-balance-presenter.service'

type BreakdownRow = MultiVisitReviewCharge & { key: string }

const PAGE_SIZE = 10

@Component({
  selector: 'mmx-balance-charge-breakdown',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CurrencyPipe, TranslateModule, MatButtonModule, MatIconModule],
  templateUrl: './balance-charge-breakdown.component.html',
  styleUrls: ['./balance-charge-breakdown.component.scss'],
})
export class BalanceChargeBreakdownComponent {
  readonly charges = input.required<readonly MultiVisitReviewCharge[]>()
  readonly total = input.required<number>()
  readonly panelId = input.required<string>()
  readonly showHeader = input(true)

  readonly searchQuery = signal('')
  readonly requestedPage = signal(0)
  readonly normalizedSearchQuery = computed(() => this.searchQuery().trim().toLocaleLowerCase())
  readonly searchId = computed(() => `${this.panelId()}-search`)
  readonly resultsId = computed(() => `${this.panelId()}-results`)
  readonly chargeCount = computed(() => this.charges().length)
  readonly hasSearchQuery = computed(() => this.normalizedSearchQuery().length > 0)
  readonly filteredRows = computed<BreakdownRow[]>(() =>
    this.charges()
      .map((charge, index) => ({ ...charge, key: `charge-${index}` }))
      .filter(row => this.matchesSearch(row)),
  )
  readonly filteredChargeCount = computed(() => this.filteredRows().length)
  readonly pageCount = computed(() => Math.max(Math.ceil(this.filteredRows().length / PAGE_SIZE), 1))
  readonly currentPage = computed(() => Math.min(this.requestedPage(), this.pageCount() - 1))
  readonly visibleRows = computed(() => {
    const start = this.currentPage() * PAGE_SIZE
    return this.filteredRows().slice(start, start + PAGE_SIZE)
  })
  readonly hasVisibleUnpricedRows = computed(() =>
    this.visibleRows().some(row => row.price == null),
  )
  readonly resultStart = computed(() => this.filteredRows().length === 0 ? 0 : this.currentPage() * PAGE_SIZE + 1)
  readonly resultEnd = computed(() => Math.min((this.currentPage() + 1) * PAGE_SIZE, this.filteredRows().length))

  onSearch(event: Event): void {
    const target = event.target
    if (target instanceof HTMLInputElement) {
      this.searchQuery.set(target.value)
      this.requestedPage.set(0)
    }
  }

  clearSearch(): void {
    this.searchQuery.set('')
    this.requestedPage.set(0)
  }

  previousPage(): void {
    this.requestedPage.update(page => Math.max(page - 1, 0))
  }

  nextPage(): void {
    this.requestedPage.update(page => Math.min(page + 1, this.pageCount() - 1))
  }

  // One input searches both columns, so uncoded lines stay findable by their description.
  private matchesSearch(charge: MultiVisitReviewCharge): boolean {
    const query = this.normalizedSearchQuery()
    return charge.cptCode.toLocaleLowerCase().includes(query)
      || charge.description.toLocaleLowerCase().includes(query)
  }
}
